import {
  computed,
  inject,
  Injectable,
  INJECTOR,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { handleRequestError } from '@softline/application';
import { MessageBarService } from '@softline/ui-core';
import { AddMileageBody } from '../models/vehicle';
import { MileageHistoryRepository } from '../repositories/mileage-history.repository';

@Injectable()
export class MileageHistoryService {
  readonly #mileageHistoryRepository = inject(MileageHistoryRepository);
  readonly messageBarService = inject(MessageBarService);
  readonly injector = inject(INJECTOR);

  readonly #id = signal<number | null>(null);

  readonly #vehicleMileageHistoryResource = resource({
    params: () => this.#id(),
    loader: async ({ params }) => {
      const vehicleId = params;
      if (!vehicleId) {
        return null;
      }
      return this.#mileageHistoryRepository.retrieveById(vehicleId);
    },
  });

  readonly mileageHistoryLoading = computed(() =>
    this.#vehicleMileageHistoryResource.isLoading()
  );

  readonly mileageHistoryError = computed(() =>
    this.#vehicleMileageHistoryResource.status() === 'error'
  );

  readonly mileageHistory = linkedSignal(() =>
    this.#vehicleMileageHistoryResource.hasValue()
      ? (this.#vehicleMileageHistoryResource.value() ?? [])
      : []
  );

  loadMileageHistory(id: number | null) {
    if (id === null) {
      this.#id.set(null);
      this.#vehicleMileageHistoryResource.set(null);
      return;
    }
    if (this.#id() === id) {
      this.#vehicleMileageHistoryResource.reload();
    } else {
      this.#id.set(id);
    }
  }

  reload(): void {
    this.#vehicleMileageHistoryResource.reload();
  }

  async addMileage(vehicleId: string, body: AddMileageBody) {
    try {
      const mileage = await this.#mileageHistoryRepository.addMileage(vehicleId, body);
      let mileageList = [...this.mileageHistory()];
      mileageList = [mileage, ...mileageList];
      this.mileageHistory.set(mileageList);
      this.messageBarService.success('Die Kilometer wurden erfolgreich hinzugefügt.');
      this.reload();
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }
}
